import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { Call, Device, type TwilioError } from '@twilio/voice-sdk'
import { nanoid } from 'nanoid'
import { type AxiosResponse, isAxiosError } from 'axios'
import { LINK_ENABLE_MICROPHONE, LINK_AIRCALL_WORKSPACE } from 'shared/constants/links'
import { toastStore } from 'shared/ui'
import { NODE_ENV } from 'shared/config'
import { logger, writeTextToClipboard } from 'shared/lib'
import { CheckInternet } from 'shared/store/CheckInternet'
import { pageWorker } from 'shared/store/pageWorker'
import { sentEventSentry } from 'entities/Sentry/lib/sentEventSentry'
import {
  type ICallStoreSignal,
  type IConnectTwilioExtraParams,
  type IResponseTwilioContact,
  type IResponseTwilioConversation,
  type IResponseTwilioInbox,
  type IUnholdCall,
} from 'entities/Call'
import { CallApi } from 'entities/Call/api/call'
import { type ICallControlErrorCode, type IResponseVoiceCall } from 'entities/Call/api/types'
import { TwilioContact } from 'entities/Call/model/TwilioContact'
import { TwilioInbox } from 'entities/Call/model/TwilioInbox'
import { TwilioConversation } from 'entities/Call/model/TwilioConversation'
import { CallEntry } from 'entities/Call/model/CallEntry'
import { Conversation, type ConversationsStore } from 'entities/Conversation'
import { UsersApi } from 'entities/Users/api/users'
import { DeviceItemsEnum, DeviceSettingsStore, managerStream } from 'entities/DeviceSettings'
import { ActiveCallingStore } from 'entities/Call/store/activeCallingStore'
import { type UsersStore } from 'entities/Users'
import { type InboxesStore } from 'entities/Inbox'
import { type ContactsStore } from 'entities/Contacts'
import type { Websocket } from 'entities/WebSocket'
import { type IParamsSendFromSmartOption } from 'entities/SendFrom'
import { type OrganizationStore } from 'entities/Organization'
import { SharedWorkerCallStore } from 'entities/Call/store/sharedWorkerCallStore'

interface TwilioSound {
  _audio?: HTMLAudioElement
  _src?: string
  url?: string
}

type HTMLAudioElementWithSinkId = HTMLAudioElement & {
  setSinkId?: (deviceId: string) => Promise<void>
}

// A transient failure fetching the Twilio voice token used to leave the device
// null/unregistered with no recovery, silently making the agent unreachable
// (inbound calls go straight to missed). Retry the token request a few times
// with a linear backoff before giving up.
const VOICE_TOKEN_RETRY_ATTEMPTS = 3
const VOICE_TOKEN_RETRY_BASE_DELAY_MS = 2000
// When every token retry fails during initDevice, re-attempt the whole device
// init after a longer delay so the agent eventually comes back online instead
// of staying dead until a manual page reload.
const INIT_DEVICE_RETRY_DELAY_MS = 15000

export class CallStore {
  private activeCallingStore
  private _device: Device | null = null
  private _connect: Call | null = null
  private _callsMap: Map<string, CallEntry> = new Map()
  private _pendingResumeLegs: Map<
    Call,
    { heldSid: string; timeoutId: ReturnType<typeof setTimeout> }
  > = new Map()
  private _outgoingSoundBeforeResume: boolean | null = null
  private readonly _RESUME_PENDING_TIMEOUT_MS = 10000
  // Safety net for the deferred held-call teardown: if Twilio never closes the
  // live leg after the "your contact has exited the call" announcement, force
  // teardown so the popup can't hang open. The announcement is ~2-3s; 8s leaves
  // ample margin. Keyed by callSid; cleared in _removeEntry.
  private _endPendingTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map()
  private readonly _ANNOUNCEMENT_TEARDOWN_TIMEOUT_MS = 8000
  private _token: string | null = null
  private _loading = true
  private _disabled = true
  private _contactTo: TwilioContact | null = null
  private _contactFrom: TwilioInbox | null = null
  private _status: Call.State = Call.State.Closed
  private _started = false
  private _direction: Call.CallDirection | null = null
  private _twilioConversation: TwilioConversation | null = null

  private _isRecord = false
  private _isRecordLoading = false
  private _isRingEverywhere = false
  private _isRingEverywhereLoading = false
  private _ringEverywhereAudioElements: HTMLAudioElement[] = []
  private _ringEverywhereStopped = false

  private _signal: ICallStoreSignal | null = null

  private _isInvalidToken = false
  private _isInitializingDevice = false
  private _initDeviceRetryTimer: ReturnType<typeof setTimeout> | null = null
  private _voicemailDropCompleted = false
  private _transferringCallSid: string | null = null

  private _deviceSettingsStore = new DeviceSettingsStore({
    items: [
      DeviceItemsEnum.audioOutput,
      DeviceItemsEnum.audioInput,
      DeviceItemsEnum.recordAuto,
      DeviceItemsEnum.defaultInbox,
    ],
  })
  private _disposeStatus: IReactionDisposer | null = null
  private _disposeSelectedAudioInput: IReactionDisposer | null = null
  private _disposeSelectedAudioOutput: IReactionDisposer | null = null
  private _disposeUserSettings: IReactionDisposer | null = null
  private _disposePrimaryPromotion: IReactionDisposer | null = null
  private _worker = pageWorker
  private _sharedWorkerCallStore: SharedWorkerCallStore

  constructor(
    private _usersStore: UsersStore,
    private _inboxesStore: InboxesStore,
    private _conversationStore: ConversationsStore,
    private _contactsStore: ContactsStore,
    private _websocket: Websocket,
    private _organizationStore: OrganizationStore
  ) {
    makeAutoObservable(this)
    this.activeCallingStore = new ActiveCallingStore(this, this._usersStore, this._websocket)
    this._sharedWorkerCallStore = new SharedWorkerCallStore(this)

    this._worker.addEventListener('message', (e) => {
      if (e.data.action === 'detect_fast') {
        if (!CheckInternet.isOnline) return
        // Defer if an init is already running or a backoff retry is already
        // scheduled. detect_fast is a recurring tick; without this it would
        // cancel the armed 15s retry (see initDevice) and re-fire every tick,
        // turning a persistently failing token endpoint into a request storm —
        // and could tear down an in-flight device via destroyDevice() below.
        if (this._isInitializingDevice || this._initDeviceRetryTimer) return
        // Re-init when the device dropped its registration OR when its token was
        // rejected as invalid/expired (error 20101). The invalid-token case used
        // to early-return here and, because _isInvalidToken was never cleared
        // anywhere, permanently disabled recovery until a full page reload —
        // silently sending inbound calls to missed. initDevice now re-fetches a
        // fresh token (with retry) and clears the flag on success.
        if (this._device?.state === 'unregistered' || this._isInvalidToken) {
          this.destroyDevice()
          this.initDevice()
        }
      }
    })

    this.reactionStatus()
    this.reactionUserSettings()
    this.reactionPrimaryPromotion()
  }

  get deviceSettingsStore() {
    return this._deviceSettingsStore
  }

  get hasActiveCall() {
    return this.activeCallingStore.hasActiveCall
  }

  reset = () => {
    this._direction = null
    this._status = Call.State.Closed
    this._twilioConversation = null
    this._transferringCallSid = null
    this.stopStreams()
    this.deviceSettingsStore.removeStream()
  }

  setVoicemailDropCompleted = (value: boolean) => {
    this._voicemailDropCompleted = value
  }

  setTransferringCallSid = (callSid: string | null) => {
    this._transferringCallSid = callSid
  }

  isOwnTransferLeg = (callSid?: string | null): boolean => {
    if (!callSid) return false
    if (callSid === this._transferringCallSid) return true
    return !!this._lookupEntry(callSid)
  }

  setCallStatus = (status: Call.State) => {
    this._status = status
  }

  initDevice = async () => {
    // Guard against overlapping inits (detect_fast + scheduled retry + mounts
    // can all fire) so we never spin up parallel token requests / devices.
    if (this._isInitializingDevice) return
    runInAction(() => {
      this._isInitializingDevice = true
    })

    try {
      const checkMicrophone = await this.checkMicrophone()

      sentEventSentry({
        event: '[WEB] Check Microphone',
        name: 'calling',
        level: 'log',
        tags: {
          enableMicrophone: checkMicrophone,
        },
        sampleRate: 0.2,
      })

      // Bail without touching a pending recovery timer: a denied/unavailable
      // mic isn't something a token re-init fixes, and cancelling a scheduled
      // retry here would strand the device offline until another trigger runs.
      if (!checkMicrophone) return

      runInAction(() => {
        this._disabled = false
      })

      // Committed to a real init now — cancel any pending scheduled retry so it
      // can't fire a duplicate init later.
      this._clearInitDeviceRetry()

      const token = await this._createVoiceTokenWithRetry()

      if (!token) {
        // Every token attempt failed. Don't leave the device null/dead with no
        // recovery (that's how an agent silently stops receiving inbound calls);
        // schedule another full init so the tab comes back online on its own.
        this._scheduleInitDeviceRetry()
        return
      }

      sentEventSentry({
        event: '[Twilio:Calling] token_created',
        name: 'calling',
        level: 'log',
        sampleRate: 0.2,
      })

      await this._usersStore.initPromise

      runInAction(async () => {
        this._token = token
        // A fresh token clears the invalid-token latch, so detect_fast recovery
        // is no longer permanently disabled after a prior 20101.
        this._isInvalidToken = false
        this._isRecord = this._usersStore.isRecordAutomatically
        this._isRingEverywhere = this._usersStore.isRingEverywhere

        this.destroyDevice()

        // https://www.twilio.com/docs/voice/sdks/javascript/twiliodevice#deviceoptions
        this._device = new Device(this._token, {
          tokenRefreshMs: 60000 * 30,
          logLevel: 3,
          codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
          allowIncomingWhileBusy: true,
        })

        this._device.audio?.incoming(false)

        this.eventsDevice()

        await this._device.register().catch((e) => {
          logger.error(e)
        })
      })
    } catch (e) {
      logger.error(e)

      sentEventSentry({
        event: '[Twilio:Calling] initDevice',
        error: e,
        name: 'calling',
        level: 'error',
        sampleRate: 0.2,
      })

      // Unexpected failure mid-init — retry rather than leaving the device in a
      // half-initialized, unreachable state.
      this._scheduleInitDeviceRetry()
    } finally {
      runInAction(() => {
        this._isInitializingDevice = false
      })
    }
  }

  // Fetch a fresh Twilio voice token, retrying a few times with a linear
  // backoff. A single transient failure previously left the device without a
  // valid token and thus unreachable for inbound calls. Returns null when every
  // attempt failed so callers can decide how to recover.
  private _createVoiceTokenWithRetry = async (): Promise<string | null> => {
    for (let attempt = 1; attempt <= VOICE_TOKEN_RETRY_ATTEMPTS; attempt++) {
      try {
        const response = await CallApi.createVoiceToken()
        return response.data.token
      } catch (e) {
        logger.error(e)

        sentEventSentry({
          event: '[Twilio:Calling] createVoiceToken failed',
          error: e,
          name: 'calling',
          level: attempt === VOICE_TOKEN_RETRY_ATTEMPTS ? 'error' : 'warning',
          tags: {
            attempt: String(attempt),
            maxAttempts: String(VOICE_TOKEN_RETRY_ATTEMPTS),
          },
          sampleRate: 1.0,
        })

        if (attempt < VOICE_TOKEN_RETRY_ATTEMPTS) {
          await new Promise((resolve) =>
            setTimeout(resolve, VOICE_TOKEN_RETRY_BASE_DELAY_MS * attempt)
          )
        }
      }
    }

    return null
  }

  private _scheduleInitDeviceRetry = () => {
    if (this._initDeviceRetryTimer) return

    this._initDeviceRetryTimer = setTimeout(() => {
      runInAction(() => {
        this._initDeviceRetryTimer = null
      })
      this.initDevice()
    }, INIT_DEVICE_RETRY_DELAY_MS)
  }

  private _clearInitDeviceRetry = () => {
    if (this._initDeviceRetryTimer) {
      clearTimeout(this._initDeviceRetryTimer)
      this._initDeviceRetryTimer = null
    }
  }

  destroyDevice = () => {
    this._device?.destroy()
  }

  dispose = () => {
    this._pendingResumeLegs.forEach((pending) => clearTimeout(pending.timeoutId))
    this._pendingResumeLegs.clear()
    this._endPendingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
    this._endPendingTimeouts.clear()
    this._disposeStatus?.()
    this._disposeSelectedAudioInput?.()
    this._disposeSelectedAudioOutput?.()
    this._disposeUserSettings?.()
    this._disposePrimaryPromotion?.()
  }

  initDevices = async () => {
    if (!this._device?.audio) return

    const outputDevices = Array.from(this._device.audio.availableOutputDevices.values())

    if (outputDevices.length) {
      await this.setOutputDevice(outputDevices[0].deviceId)
    }
  }

  checkMicrophone = (): Promise<boolean> => {
    return new Promise(async (resolve) => {
      try {
        const stream = await window.navigator.mediaDevices.getUserMedia({ audio: true })

        managerStream.stopTrack(stream)

        resolve(true)
      } catch (e) {
        resolve(false)
      }
    })
  }

  setInputDevice = async (id: string) => {
    if (!this._device?.audio) return

    try {
      if (this._device.audio.inputDevice?.deviceId === id) return

      await this._device.audio.setInputDevice(id)

      if (this._device.audio.inputStream) {
        managerStream.addStream('twilio', this._device.audio.inputStream)
      }
    } catch (e) {
      logger.error(e)
    }
  }

  setOutputDevice = async (id: string) => {
    if (!this._device?.audio) return

    try {
      await this._device.audio.speakerDevices.set(id)
      await this._device.audio.ringtoneDevices.set(id)
    } catch (e) {
      logger.error(e)
    }
  }

  updateConnect = (connect?: Call) => {
    this._connect = connect ? connect : this._connect

    if (this._connect) {
      // Mirror _promoteNextPrimary's special case: if the primary is a
      // SDK-closed held entry (waiting on resume / customer parked in hold
      // queue), keep _status as Open so the popup stays visible. Otherwise
      // we'd read the dead SDK leg's 'closed' state and hide the popup —
      // even though the user still has held cards to interact with.
      const entry = this._lookupEntry(this._connect.parameters.CallSid)
      const liveStatus = this._connect.status()
      this._status =
        entry?.isHold && entry.isSdkLegClosed && liveStatus === Call.State.Closed
          ? Call.State.Open
          : liveStatus
      this._direction = this._connect.direction
    }
  }

  hideCall = async () => {
    this.stopRingEverywhere()

    this.reset()
    await this._device?.audio?.unsetInputDevice()
  }

  // events device
  eventsDevice = () => {
    if (this._device) {
      this._device.on('error', this.eventDeviceError)
      this._device.on('incoming', this.eventDeviceIncoming)
      this._device.on('registered', this.eventDeviceRegistered)
      this._device.on('registering', this.eventDeviceRegistering)
      this._device.on('unregistered', this.eventDeviceUnregistered)
      this._device.on('tokenWillExpire', this.eventDeviceTokenWillExpire)
      this._device.on('destroyed', this.eventDeviceDestroyed)
      this._device?.audio?.on('deviceChange', this.eventDeviceAudioDeviceChange)
    }
  }

  eventDeviceRegistering = () => {
    sentEventSentry({
      event: '[Twilio:Calling] Device Registering',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })
  }

  eventDeviceDestroyed = () => {
    sentEventSentry({
      event: '[Twilio:Calling] Device Destroyed',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })
  }

  // https://www.twilio.com/docs/api/errors/20101
  // https://www.twilio.com/docs/api/errors/20104
  // https://www.twilio.com/docs/api/errors/53001
  // https://www.twilio.com/docs/voice/sdks/error-codes
  eventDeviceError = (error: TwilioError.TwilioError) => {
    this.updateConnect()
    this.log('[Twilio] - eventDeviceError')

    const twilioError = error.originalError?.twilioError

    // Invalid access token
    if (error.code === 20101) {
      this._isInvalidToken = true
    }

    if (error.code === 31000) {
      if (twilioError?.code === 53001) CheckInternet.check()
    }

    if (error.code === 53001) CheckInternet.check()
    if (error.code === 31009) CheckInternet.check()
    if (error.code === 31005) CheckInternet.check()
    if (error.code === 53000) CheckInternet.check()

    const level1 = twilioError ? ' Level 1' : ''

    sentEventSentry({
      event: `[Twilio:Calling] Device Error${level1}`,
      error: error,
      name: 'calling',
      level: 'error',
      sampleRate: 0.2,
    })

    if (twilioError) {
      sentEventSentry({
        event: '[Twilio:Calling] Device Error Level 2',
        error: twilioError,
        name: 'calling',
        level: 'error',
        sampleRate: 0.2,
      })
    }
  }

  eventDeviceIncoming = (connect: Call) => {
    const incomingSid = connect.parameters.CallSid

    // "First" means there's no genuinely live call to keep in the foreground —
    // not merely that the map is empty. A leaked dead entry (closed but not yet
    // removed from _callsMap) must NOT suppress the incoming signal/contact
    // refresh; primaryEntry is null unless an entry is active/held/ringing, so a
    // real multi-call still preserves the primary's context (isFirst === false).
    const isFirst = this.primaryEntry === null
    if (isFirst) {
      this.updateConnect(connect)
    }
    this.eventsConnect(connect)

    sentEventSentry({
      event: '[Twilio:Calling] Incoming: received',
      name: 'calling',
      level: 'log',
      tags: {
        callSid: incomingSid,
        hasDevice: String(!!this._device),
        deviceState: this._device?.state ?? 'none',
        isWorkerActive: String(this._sharedWorkerCallStore.isWorkerActive),
        callsInMap: String(this._callsMap.size),
      },
      sampleRate: 1.0,
    })

    try {
      const conversationPayload: IResponseTwilioConversation = JSON.parse(
        connect.customParameters.get('conversation') || ''
      )

      const contactPayload: IResponseTwilioContact = JSON.parse(
        connect.customParameters.get('contact') || ''
      )

      const inboxPayload: IResponseTwilioInbox = JSON.parse(
        connect.customParameters.get('inbox') || ''
      )

      const contactTo = new TwilioContact(contactPayload)
      const contactFrom = new TwilioInbox(inboxPayload)
      const twilioConversation = new TwilioConversation(conversationPayload)

      const isAway = this._usersStore.isAwayStatus
      const shouldMute = contactFrom.isMuted || isAway || this.hasActiveCall

      if (shouldMute) {
        sentEventSentry({
          event: '[Twilio:Calling] Incoming: muted',
          name: 'calling',
          level: 'log',
          tags: {
            callSid: connect.parameters.CallSid,
            reason_isMuted: String(contactFrom.isMuted),
            reason_isAway: String(isAway),
            reason_hasActiveCall: String(this.hasActiveCall),
          },
          sampleRate: 1.0,
        })
      }

      runInAction(() => {
        this._callsMap.set(
          connect.parameters.CallSid,
          new CallEntry({
            call: connect,
            contact: contactTo,
            inbox: contactFrom,
            conversation: twilioConversation,
            direction: Call.CallDirection.Incoming,
          })
        )

        if (isFirst) {
          this._contactTo = contactTo
          this._contactFrom = contactFrom
          this._twilioConversation = twilioConversation
          this._signal = {
            key: nanoid(),
            name: 'incoming',
          }
        }
      })

      if (this._sharedWorkerCallStore.isWorkerActive) {
        // Tabs that are muted/away/busy don't claim ringing — the worker will
        // route the call to a free candidate from the same identity.
        if (!shouldMute) {
          sentEventSentry({
            event: '[Twilio:Calling] Incoming: notifyWorker',
            name: 'calling',
            level: 'log',
            tags: { callSid: connect.parameters.CallSid },
            sampleRate: 1.0,
          })
          this._sharedWorkerCallStore.notifyIncomingCall(connect.parameters.CallSid)
        }
      } else {
        // Fallback for browsers without SharedWorker — preserve old behavior.
        this.handleMuteIncomingNotification(shouldMute)
      }

      this.log('[Twilio] - eventDeviceIncoming')
    } catch (e) {
      logger.error(e)
      this.handleMuteIncomingNotification(true)
      sentEventSentry({
        event: '[Twilio:Calling] Incoming: unhandled error',
        error: e,
        name: 'calling',
        level: 'error',
        sampleRate: 1.0,
      })
    }
  }

  eventDeviceRegistered = async () => {
    this.updateConnect()
    this.log('[Twilio] - eventDeviceRegistered')

    sentEventSentry({
      event: '[Twilio:Calling] Device Registered',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })

    await this.initDevices()

    runInAction(() => {
      this._loading = false
    })
  }

  eventDeviceUnregistered = () => {
    this.updateConnect()
    this.log('[Twilio] - eventDeviceUnregistered')

    sentEventSentry({
      event: '[Twilio:Calling] Device Unregistered',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })
  }

  eventDeviceTokenWillExpire = async () => {
    this.updateConnect()
    this.log('[Twilio] - eventDeviceTokenWillExpire')

    sentEventSentry({
      event: '[Twilio:Calling] Device TokenWillExpire',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })

    const token = await this._createVoiceTokenWithRetry()

    if (token) {
      this._device?.updateToken(token)
      runInAction(() => {
        this._isInvalidToken = false
      })
      return
    }

    // Every refresh attempt failed: the current token will expire and the
    // device will unregister, silently sending inbound calls to missed. Re-init
    // the device (fresh token + re-register) instead of just logging.
    sentEventSentry({
      event: '[Twilio:Calling] Device TokenWillExpire: refresh failed, scheduling re-init',
      name: 'calling',
      level: 'error',
      sampleRate: 1.0,
    })

    this._scheduleInitDeviceRetry()
  }

  eventDeviceAudioDeviceChange = () => {
    this.updateConnect()
    this.log('[Twilio] - eventDeviceAudioDeviceChange')
  }

  // events connect
  eventsConnect = (call?: Call) => {
    const target = call ?? this._connect
    if (!target) return

    // Twilio SDK invokes 'cancel' with no args; bind via closure to recover CallSid.
    target.on('cancel', () => this.eventConnectCancel(target))
    target.on('accept', (call: Call) => this.eventConnectAccept(call))
    target.on('disconnect', (call: Call) => this.eventConnectDisconnect(call))
    target.on('error', (error: TwilioError.TwilioError) => this.eventConnectError(error, target))
    target.on('mute', (isMuted: boolean, call: Call) => this.eventConnectMute(isMuted, call))
    target.on('reconnected', () => this.eventConnectReconnected(target))
    target.on('reconnecting', this.eventConnectReconnecting)
    target.on('reject', () => this.eventConnectReject(target))
    target.on('ringing', () => this.eventConnectRinging(target))
    target.on('sample', this.eventConnectSample)
    target.on('warning', this.eventConnectWarning)
  }

  eventConnectWarning = (warningName: string, warningData: string) => {
    if (!this._connect) return

    const params = {
      warning: warningName,
      details: warningData,
    }

    this.log('[Twilio] - eventConnectWarning')

    sentEventSentry({
      event: '[Twilio:Calling] Event ConnectWarning',
      name: 'calling',
      level: 'warning',
      tags: {
        data: JSON.stringify(params),
      },
      sampleRate: 0.2,
    })

    return CallApi.updateVoiceCallBySidQualityLog(this._connect.parameters.CallSid, params)
      .then((response: AxiosResponse) => {
        return response.data.success
      })
      .catch((e) => {
        return e.response
      })
  }

  // Twilio Voice SDK 2.12.4 emits 'cancel' with no arguments (call.js:267).
  // Pre-multi-call we resolved the call from this._connect; now the closure in
  // eventsConnect() captures the target Call object so we can identify it even
  // when it's a secondary/held entry rather than primary.
  eventConnectCancel = (call: Call) => {
    const callSid = call.parameters.CallSid

    // A resume leg that cancelled before connecting: keep the customer parked.
    if (this._revertResumeLegIfPending(call)) {
      this.stopRing(callSid)
      this.log('[Twilio] - eventConnectCancel (resume leg reverted to held)')
      return
    }

    const wasPrimary = this._connect?.parameters.CallSid === callSid

    this.stopRing(callSid)
    this._sharedWorkerCallStore.notifyCallEnded(callSid)

    const cancelled = this._lookupEntry(callSid)
    if (cancelled) this._removeEntry(cancelled.callSid)

    if (!wasPrimary) {
      this.log('[Twilio] - eventConnectCancel (secondary)')
      return
    }

    if (this._callsMap.size > 0) {
      this._promoteNextPrimary()
      this.log('[Twilio] - eventConnectCancel (primary promoted)')
      return
    }

    this.updateConnect()
    this._sharedWorkerCallStore.setBusy(false)
    this.hideCall()
    this.log('[Twilio] - eventConnectCancel')
  }

  eventConnectAccept = async (connect: Call) => {
    const callSid = connect.parameters.CallSid
    // A resume leg that connects must attach to its held entry (and clear hold)
    // before the normal accept flow promotes it to primary.
    this._commitResumeLegIfPending(connect)
    this._adoptCallSid(connect, callSid)
    const wasPrimary = this._connect?.parameters.CallSid === callSid

    this.stopRing(callSid)
    this._sharedWorkerCallStore.notifyCallAccepted(callSid)
    this._sharedWorkerCallStore.setBusy(true)

    this._lookupEntry(callSid)?.syncStatus()

    if (wasPrimary) {
      this.updateConnect(connect)
    } else {
      this._promoteNextPrimary()
    }

    const isPrimaryNow = this._connect?.parameters.CallSid === callSid
    if (isPrimaryNow) {
      this.activeCallingStore.sendCallingEvent(true)
      this.activeCallingStore.sendConversationCallingEvent(true)
    }
    this.log('[Twilio] - eventConnectAccept')

    if (this._device?.audio) {
      const selectedDevice = this.deviceSettingsStore.device.audioOutput
      if (selectedDevice?.deviceId) {
        try {
          if (
            this._device.audio.speakerDevices &&
            typeof this._device.audio.speakerDevices.set === 'function'
          ) {
            await this._device.audio.speakerDevices.set(selectedDevice.deviceId)
          }
        } catch (e) {
          logger.error(`[eventConnectAccept] Failed to set speaker device: ${e}`)
        }
      }
    }
  }

  eventConnectDisconnect = (connect: Call) => {
    const callSid = connect.parameters.CallSid

    // A resume leg that dropped before connecting: keep the customer parked.
    if (this._revertResumeLegIfPending(connect)) {
      this.stopRing(callSid)
      this.log('[Twilio] - eventConnectDisconnect (resume leg reverted to held)')
      return
    }

    const wasPrimary = this._connect?.parameters.CallSid === callSid

    this.stopRing(callSid)

    const entry = this._lookupEntry(callSid)
    const keptAsHeld = !!entry && (entry.isHold || entry.isHoldPending) && !entry.isEndPending
    if (keptAsHeld) {
      entry.setSdkLegClosed(true)
      entry.syncStatus()
    } else {
      this._sharedWorkerCallStore.notifyCallEnded(callSid)
      this._removeEntry(entry?.callSid ?? callSid)
    }

    if (!wasPrimary) {
      this.log('[Twilio] - eventConnectDisconnect (secondary)')
      return
    }

    if (this._callsMap.size > 0) {
      this._promoteNextPrimary()
      this.log('[Twilio] - eventConnectDisconnect (primary promoted)')
      return
    }

    this._sharedWorkerCallStore.setBusy(false)
    this.activeCallingStore.sendCallingEvent(false)
    this.activeCallingStore.sendConversationCallingEvent(false)
    this.updateConnect(connect)
    this.log('[Twilio] - eventConnectDisconnect')
    this.hideCall()
    this._device?.audio?.unsetInputDevice()
  }

  private _adoptCallSid = (connect: Call, callSid: string) => {
    if (!callSid) return
    const entry = this._lookupEntry(callSid)
    if (!entry) return
    if (entry.callSid === callSid) return

    const oldKey = entry.callSid
    runInAction(() => {
      entry.setCallSid(callSid)
      this._callsMap.delete(oldKey)
      this._callsMap.set(callSid, entry)
    })
  }

  private _lookupEntry = (sid: string): CallEntry | undefined => {
    const direct = this._callsMap.get(sid)
    if (direct) return direct
    for (const entry of this._callsMap.values()) {
      if (entry.call.parameters.CallSid === sid) return entry
    }
    return undefined
  }

  // SWR-23892: when /unhold returns mode:"new_leg", the agent leg of the held
  // call is dead and the FE must originate the resume leg itself (BE bridges it
  // to the parked customer). Uses the same outbound params as a normal call so
  // the voice webhook authorizes, plus the single-use resumeToken.
  originateResumeLeg = async (callSid: string, resumeToken: string): Promise<boolean> => {
    const entry = this._lookupEntry(callSid)
    if (!entry) {
      return false
    }
    const device = this._device
    if (!device) {
      entry.setResumePending(false)
      return false
    }

    const heldSid = entry.callSid

    this._abandonPendingResumeLegsFor(heldSid)

    // Twilio's Device permits a single live SDK leg (device._activeCall) and
    // device.connect() throws "A Call is already active" while one exists. Free
    // it first by disconnecting whichever held leg holds that slot; it parks
    // server-side (eventConnectDisconnect → keptAsHeld), mirroring what the SDK's
    // own beforeAccept does for a second incoming call.
    await this._freeDeviceForResume()

    // Mute the outgoing dialtone for the resume leg — resuming a parked customer
    // should be silent, not sound like a fresh outbound call. Restored on every
    // terminal path (commit / revert / timeout / catch) via _restoreOutgoingSound.
    const audio = device.audio
    if (audio?.outgoing) {
      if (this._outgoingSoundBeforeResume === null) {
        this._outgoingSoundBeforeResume = audio.outgoing()
      }
      audio.outgoing(false)
    }

    try {
      const connect = await device.connect({
        params: {
          To: entry.contact.number,
          ...(typeof entry.conversation?.id === 'number' && {
            ConversationId: String(entry.conversation.id),
          }),
          resumeToken,
        },
      })

      const timeoutId = setTimeout(
        () => this._onResumeLegTimeout(connect),
        this._RESUME_PENDING_TIMEOUT_MS
      )
      this._pendingResumeLegs.set(connect, { heldSid, timeoutId })
      this.eventsConnect(connect)
      return true
    } catch (e) {
      // device.connect() rejected — no Twilio events will fire. The customer is
      // still parked, so keep the card held and let the user retry. Do NOT force
      logger.error(e)
      this._restoreOutgoingSound()
      runInAction(() => {
        entry.setResumePending(false)
        entry.setIsHold(true)
      })
      return false
    }
  }

  // Release the device's single live SDK leg so a resume leg can be originated.
  // The live slot is held by whichever leg the SDK considers active — usually the
  // call we just put on hold, but it can also be the resumed call's own leg if
  // its hold hasn't closed it yet. Disconnecting a held leg parks it (keptAsHeld)
  // rather than ending it, so freeing it here is safe. No-op when device is free.
  private _freeDeviceForResume = async (): Promise<void> => {
    const device = this._device
    if (!device || !device.isBusy) return

    const live = this.allCallEntries.find((e) => !e.isSdkLegClosed && e.isHold && !e.isEndPending)
    if (live) {
      live.call.disconnect()
    }

    await this._waitUntil(() => !device.isBusy, 2000)
  }

  private _waitUntil = (predicate: () => boolean, timeoutMs: number): Promise<boolean> => {
    return new Promise((resolve) => {
      if (predicate()) {
        resolve(true)
        return
      }
      const start = Date.now()
      const interval = setInterval(() => {
        if (predicate()) {
          clearInterval(interval)
          resolve(true)
        } else if (Date.now() - start > timeoutMs) {
          clearInterval(interval)
          resolve(false)
        }
      }, 50)
    })
  }

  // Restore the device dialtone to whatever it was before we muted it for a
  // resume leg. Idempotent: safe to call from multiple terminal paths.
  private _restoreOutgoingSound = () => {
    if (this._outgoingSoundBeforeResume === null) return
    this._device?.audio?.outgoing?.(this._outgoingSoundBeforeResume)
    this._outgoingSoundBeforeResume = null
  }

  private _onResumeLegTimeout = (call: Call) => {
    const pending = this._pendingResumeLegs.get(call)
    if (!pending) return
    this._restoreOutgoingSound()
    const entry = this._callsMap.get(pending.heldSid)
    if (entry && entry.isResumePending) {
      entry.setResumePending(false)
    }
    this._abandonResumeLeg(call)
  }

  private _abandonResumeLeg = (call: Call) => {
    const pending = this._pendingResumeLegs.get(call)
    if (pending) {
      clearTimeout(pending.timeoutId)
      this._pendingResumeLegs.delete(call)
    }
    try {
      call.disconnect()
    } catch (e) {
      logger.error(e)
    }
  }

  private _abandonPendingResumeLegsFor = (heldSid: string) => {
    const stale = [...this._pendingResumeLegs.entries()].filter(
      ([, pending]) => pending.heldSid === heldSid
    )
    stale.forEach(([call]) => this._abandonResumeLeg(call))
  }

  // Commit-on-connect: the resume leg actually connected. Attach it to the held
  // entry, re-key to the new CallSid, and clear the hold state.
  private _commitResumeLegIfPending = (connect: Call): boolean => {
    const pending = this._pendingResumeLegs.get(connect)
    if (!pending) return false

    // CallSid is available now (on 'accept'), unlike at device.connect() time.
    const sid = connect.parameters.CallSid

    clearTimeout(pending.timeoutId)
    this._pendingResumeLegs.delete(connect)
    this._restoreOutgoingSound()

    const entry = this._callsMap.get(pending.heldSid)
    if (!entry) {
      try {
        connect.disconnect()
      } catch (e) {
        logger.error(e)
      }
      return false
    }

    runInAction(() => {
      entry.replaceCall(connect)
      if (sid && pending.heldSid !== sid) {
        this._callsMap.delete(pending.heldSid)
        entry.setCallSid(sid)
        this._callsMap.set(sid, entry)
      }
      entry.setIsHold(false)
      entry.setHoldConfirmed(false)
      entry.setResumePending(false)
      entry.setSdkLegClosed(false)
    })
    return true
  }

  // Resume leg failed/cancelled before connecting — the customer is still parked.
  // Revert the entry to held under its original sid; do NOT remove the card.
  private _revertResumeLegIfPending = (call: Call): boolean => {
    const pending = this._pendingResumeLegs.get(call)
    if (!pending) return false

    clearTimeout(pending.timeoutId)
    this._pendingResumeLegs.delete(call)
    this._restoreOutgoingSound()

    const entry = this._callsMap.get(pending.heldSid)
    if (entry) {
      runInAction(() => {
        entry.setResumePending(false)
        entry.setIsHold(true)
        entry.setSdkLegClosed(true)
      })
    }
    return true
  }

  eventConnectError = (error: TwilioError.TwilioError, call?: Call) => {
    // A resume leg that errored before connecting: keep the customer parked and
    // clear isResumePending so the user can retry without waiting for the resume
    // timeout. Mirrors the cancel/reject/disconnect terminal handlers.
    if (call && this._revertResumeLegIfPending(call)) {
      this.stopRing(call.parameters.CallSid)
    }

    this.updateConnect()
    this.log('[Twilio] - eventConnectError')

    const twilioError = error.originalError?.twilioError
    const level1 = twilioError ? ' Level 1' : ''

    sentEventSentry({
      event: `[Twilio:Calling] Event ConnectError${level1}`,
      error: error,
      name: 'calling',
      level: 'error',
      sampleRate: 0.2,
    })

    if (twilioError) {
      sentEventSentry({
        event: '[Twilio:Calling] Event ConnectError Level 2',
        error: twilioError,
        name: 'calling',
        level: 'error',
        sampleRate: 0.2,
      })
    }
  }

  // Twilio Voice SDK 2.12.4 emits 'mute' as (isMuted: boolean, call: Call)
  // (call.js:1009) — the first argument is a boolean, not a string.
  eventConnectMute = (isMuted: boolean, connect: Call) => {
    const callSid = connect.parameters.CallSid
    if (this._connect?.parameters.CallSid === callSid) {
      this.updateConnect(connect)
    }
    this._lookupEntry(callSid)?.syncStatus()
    this.log('[Twilio] - eventConnectMute')

    sentEventSentry({
      event: '[Twilio:Calling] Event ConnectMute',
      name: 'calling',
      level: 'log',
      tags: {
        enableMute: isMuted,
      },
      sampleRate: 0.2,
    })
  }

  // Twilio Voice SDK 2.12.4 emits 'reconnected' with no arguments (call.js:394, 516).
  eventConnectReconnected = (call?: Call) => {
    const callSid = call?.parameters.CallSid
    if (callSid) {
      this._lookupEntry(callSid)?.syncStatus()
    }
    if (!callSid || this._connect?.parameters.CallSid === callSid) {
      this.updateConnect()
    }
    this.log('[Twilio] - eventConnectReconnected')

    sentEventSentry({
      event: '[Twilio:Calling] Event Reconnected',
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })
  }

  // Twilio Voice SDK 2.12.4 emits 'reconnecting' with a TwilioError, not a generic Error
  // (call.js:377, 532). Using TwilioError.TwilioError preserves .code / .originalError.
  eventConnectReconnecting = (error: TwilioError.TwilioError) => {
    this.log('[Twilio] - eventConnectReconnecting')

    sentEventSentry({
      event: '[Twilio:Calling] Event Reconnecting',
      error: error,
      name: 'calling',
      level: 'log',
      sampleRate: 0.2,
    })
  }

  eventConnectReject = (call?: Call) => {
    const callSid = call?.parameters.CallSid ?? this._connect?.parameters.CallSid

    // A resume leg that was rejected before connecting: keep the customer parked.
    if (call && this._revertResumeLegIfPending(call)) {
      if (callSid) this.stopRing(callSid)
      this.log('[Twilio] - eventConnectReject (resume leg reverted to held)')
      return
    }

    const wasPrimary = !!callSid && this._connect?.parameters.CallSid === callSid

    if (callSid) {
      this.stopRing(callSid)
      this._sharedWorkerCallStore.notifyCallEnded(callSid)
      const rejected = this._lookupEntry(callSid)
      this._removeEntry(rejected?.callSid ?? callSid)
    }

    if (!wasPrimary) {
      this.log('[Twilio] - eventConnectReject (secondary)')
      return
    }

    if (this._callsMap.size > 0) {
      this._promoteNextPrimary()
      this.log('[Twilio] - eventConnectReject (primary promoted)')
      return
    }

    this.updateConnect()
    this._sharedWorkerCallStore.setBusy(false)
    this.log('[Twilio] - eventConnectReject')
  }

  eventConnectRinging = (call?: Call) => {
    const callSid = call?.parameters.CallSid
    if (callSid) {
      this._lookupEntry(callSid)?.syncStatus()
    }
    if (!callSid || this._connect?.parameters.CallSid === callSid) {
      this.updateConnect()
    }
    this.log('[Twilio] - eventConnectRinging')
  }

  eventConnectSample = () => {
    this.log('[Twilio] - eventConnectSample')
  }

  log = (title: string) => {
    if (NODE_ENV !== 'production') {
      console.log(title, this._status)
    }
  }

  // other methods
  connectTwilio = async (conversationId: number, extraParams?: IConnectTwilioExtraParams) => {
    if (this._status !== Call.State.Closed) return console.warn('Connection already opened')

    this.setStartedCall(true)

    const conversation = await this._conversationStore.getById({
      id: conversationId,
    })

    if (!conversation) return

    const contactFrom = await this._inboxesStore.getById(conversation.inbox_id, true)
    const contactTo = await this._contactsStore.getById(conversation.contact_id, true)

    if (contactFrom?.type !== 'inbox') return
    if (!contactTo) return
    if (
      conversation?.is_aircall ||
      this._organizationStore.organization_call?.call_via_aircall?.is_active ||
      contactFrom?.call_via_aircall?.is_active
    ) {
      if (contactTo?.number) {
        writeTextToClipboard(contactTo.number).then(() => {
          window.open(LINK_AIRCALL_WORKSPACE, '_blank')
        })

        this.setStartedCall(false)
      }

      return
    }

    const inboxNumber = contactFrom.origin?.number
    runInAction(() => {
      this._contactTo = new TwilioContact({
        id: contactTo.id,
        first_name: contactTo.first_name,
        last_name: contactTo.last_name,
        full_name: contactTo.full_name,
        number: contactTo.number,
        national_number: contactTo.national_number,
        formatted_number: contactTo.formatted_number,
        photo_url: contactTo.photo_url,
        email: contactTo.email,
      })
      this._contactFrom = new TwilioInbox({
        id: contactFrom.id,
        name: contactFrom.name,
        is_muted: contactFrom.is_muted,
        number: inboxNumber
          ? {
              id: inboxNumber.id,
              number: inboxNumber.number,
              national_number: inboxNumber.national_number,
              formatted_number: inboxNumber.formatted_number,
            }
          : null,
      })
      this._twilioConversation = new TwilioConversation({
        id: conversation.id,
        inbox_id: conversation.inbox_id,
        contact_id: conversation.contact_id,
        number_id: conversation.number_id,
      })
      this._signal = {
        key: nanoid(),
        name: 'outgoing',
      }
    })

    const isAllowed = await this.checkMicrophone()
    if (!isAllowed) {
      toastStore.add({
        type: 'error',
        title: 'Microphone access required',
        desc: 'To enable calling, please allow Salesmsg to access your microphone.',
        action: {
          link: LINK_ENABLE_MICROPHONE,
          text: 'Learn more',
        },
      })

      this.setStartedCall(false)

      return
    }

    return new Promise<void>(async (resolve) => {
      if (!this._device) return resolve()
      if (!contactTo?.number) return resolve()

      // Mark this tab busy so the worker doesn't land a parallel inbound here.
      // From this point on, every exit path must reset busy via one of the
      // Twilio connect-event handlers (accept/disconnect/cancel/reject) or
      // via the catch below.
      this._sharedWorkerCallStore.setBusy(true)

      try {
        const connect = await this._device.connect({
          params: {
            To: contactTo.number,
            ConversationId: String(conversation.id),
            ...(extraParams?.powerDialerId && { PowerDialerId: String(extraParams.powerDialerId) }),
          },
        })

        runInAction(() => {
          this._connect = connect
          if (this._contactTo && this._contactFrom) {
            this._callsMap.set(
              connect.parameters.CallSid,
              new CallEntry({
                call: connect,
                contact: this._contactTo,
                inbox: this._contactFrom,
                conversation: this._twilioConversation,
                direction: Call.CallDirection.Outgoing,
              })
            )
          }
        })

        this.eventsConnect(connect)

        const dispose = reaction(
          () => this._status,
          (status) => {
            if (status === Call.State.Closed) {
              dispose()
              this.setStartedCall(false)
              resolve()
            }
          }
        )
      } catch (e) {
        // device.connect() rejected — no Twilio events will fire, so we have
        // to clear busy ourselves; otherwise the worker keeps routing
        // inbound calls away from this tab forever.
        this._sharedWorkerCallStore.setBusy(false)
        this.setStartedCall(false)
        logger.error(e)
        resolve()
      }
    })
  }

  makeCall = async ({
    type,
    payload,
  }: {
    type: 'phoneNumber'
    payload: {
      phoneNumber: string
      inbox_id: number
      number_id?: number
      smart_option?: IParamsSendFromSmartOption
    }
  }) => {
    if (type === 'phoneNumber') {
      const { phoneNumber, number_id, inbox_id, smart_option } = payload
      const contact = await this._contactsStore.getContactByNumber(phoneNumber, true)
      if (contact) {
        const conversation = await this._conversationStore.createConversation({
          contact_id: contact.id,
          team_id: inbox_id,
          number_id,
          smart_option,
        })

        if (conversation instanceof Conversation) {
          return this.connectTwilio(conversation.id)
        }
      }
    }
  }

  disconnectTwilio = async (isClientDisconnect = true): Promise<boolean> => {
    const primary = this.primaryEntry
    const hasMultipleCalls = this._callsMap.size > 1

    if (isClientDisconnect) {
      if (primary) {
        if (primary.isHold) {
          await this.disconnectCall(primary.callSid, true)
          if (this._callsMap.size > 0) return true
          this._device?.disconnectAll()
          await this.hideCall()
          return true
        }
        if (primary.isRinging) {
          primary.call.reject()
        } else {
          primary.call.disconnect()
        }
      } else if (this._status === Call.State.Pending) {
        this._connect?.reject()
      }
    }

    // When other calls are still in the map (ringing/held secondaries), do NOT
    // force-kill them all with disconnectAll(); let the Twilio events for the
    // primary settle and the secondary legs persist. The hideCall + map cleanup
    // for the primary still runs inside eventConnectDisconnect/Reject.
    if (hasMultipleCalls) {
      if (!isClientDisconnect && primary && !primary.isRinging) {
        await this.disconnectCall(primary.callSid, true)
        return true
      }
      return isClientDisconnect
    }

    this._device?.disconnectAll()
    await this.hideCall()
    return true
  }

  acceptTwilio = () => {
    this._connect?.accept()
    this.updateConnect()
  }

  // Multi-call entry points. Pre-multi-call code routes through acceptTwilio /
  // disconnectTwilio which still target the primary call; these accept a sid so
  // upstream stores can drive ringing/held secondaries independently.
  acceptCall = (callSid: string) => {
    const entry = this._lookupEntry(callSid)
    entry?.call.accept()
    entry?.syncStatus()
  }

  disconnectCall = async (callSid: string, isClientDisconnect = true) => {
    const entry = this._lookupEntry(callSid)
    if (!entry) return

    if (!isClientDisconnect) return

    if (entry.isHold) {
      const liveSid = entry.call.parameters.CallSid
      let controlError: ReturnType<typeof this._extractControlError> = null
      try {
        await CallApi.updateVoiceCallCallEnd(liveSid)
      } catch (e) {
        logger.error(e)
        controlError = this._extractControlError(e)
      }
      if (!entry.isSdkLegClosed) {
        try {
          entry.call.disconnect()
        } catch (e) {
          logger.error(e)
        }
      }
      if (controlError) this._toastControlError(controlError.message)
      // Pair the entry removal with the worker notification: notifyCallEnded
      // was skipped in eventConnectDisconnect because the entry was kept as
      // held; now that the user has truly ended the call, tell the worker.
      this._sharedWorkerCallStore.notifyCallEnded(entry.callSid)
      runInAction(() => {
        this._removeEntry(entry.callSid)
        this._promoteNextPrimary()
      })
      if (this._callsMap.size === 0) {
        this._resetIdleState()
      }
      return
    }

    const status = entry.call.status()
    if (status === Call.State.Pending) {
      let controlError: ReturnType<typeof this._extractControlError> = null
      try {
        await CallApi.updateVoiceCallCallEnd(entry.callSid)
      } catch (e) {
        logger.error(e)
        controlError = this._extractControlError(e)
      }
      // reject() drops the entry locally via eventConnectReject regardless; on a
      // control error also surface a clear message instead of a silent failure.
      if (controlError) this._toastControlError(controlError.message)
      this._sharedWorkerCallStore.notifyCallEnded(entry.callSid)
      entry.call.reject()
    } else {
      entry.call.disconnect()
    }
  }

  endCallFromServer = (callSid: string) => {
    const entry = this._lookupEntry(callSid)
    if (!entry) return

    if (!entry.isSdkLegClosed && (entry.isHold || entry.isEndPending)) {
      entry.setEndPending(true)
      this._scheduleAnnouncementTeardownFallback(entry.callSid)
      return
    }

    this._teardownEndedCall(entry, callSid)
  }

  private _teardownEndedCall = (entry: CallEntry, callSid: string) => {
    const liveSid = entry.call.parameters.CallSid
    const wasPrimary = this._connect?.parameters.CallSid === liveSid

    this.stopRing(callSid)
    if (!entry.isSdkLegClosed) {
      try {
        entry.call.disconnect()
      } catch (e) {
        logger.error(e)
      }
    }
    this._sharedWorkerCallStore.notifyCallEnded(callSid)
    this._removeEntry(entry.callSid)

    if (!wasPrimary) {
      this.log('[Twilio] - endCallFromServer (secondary)')
      return
    }

    if (this._callsMap.size > 0) {
      this._promoteNextPrimary()
      this.log('[Twilio] - endCallFromServer (primary promoted)')
      return
    }

    this._connect = null
    this._sharedWorkerCallStore.setBusy(false)
    this.activeCallingStore.sendCallingEvent(false)
    this.activeCallingStore.sendConversationCallingEvent(false)
    this.hideCall()
    this.log('[Twilio] - endCallFromServer')
  }

  endTransferredCall = (agentCallSid?: string | null) => {
    const stored = this._transferringCallSid
    this._transferringCallSid = null

    const targetSid =
      (agentCallSid && this._lookupEntry(agentCallSid) ? agentCallSid : null) ??
      (stored && this._lookupEntry(stored) ? stored : null) ??
      (this.primaryEntry?.isHold ? this.primaryEntry.callSid : null)

    if (!targetSid) return
    this.endCallFromServer(targetSid)
  }

  holdCall = async (callSid: string, sendSegmentEvent = false) => {
    const entry = this._lookupEntry(callSid)
    if (!entry) return false
    if (entry.isHold) return true

    entry.setHoldPending(true)

    try {
      const { data } = await CallApi.updateVoiceCallHold({
        call_sid: entry.callSid,
        send_segment_event: sendSegmentEvent,
        is_first_hold: entry.isFirstHold,
      })
      if (data.success) {
        entry.setIsHold(true)
        return true
      }
      const controlError = this._extractControlError(null, data)
      if (controlError) {
        this._dropStaleEntry(entry, controlError.message)
        return false
      }
    } catch (e) {
      logger.error(e)
      const controlError = this._extractControlError(e)
      if (controlError) {
        this._dropStaleEntry(entry, controlError.message)
        return false
      }
    } finally {
      entry.setHoldPending(false)
      if (entry.isSdkLegClosed && !entry.isHold) {
        runInAction(() => {
          this._removeEntry(entry.callSid)
          this._promoteNextPrimary()
        })
      }
    }
    return false
  }

  unholdCall = async (callSid: string, sendSegmentEvent = false): Promise<IUnholdCall> => {
    const entry = this._lookupEntry(callSid)
    if (!entry) {
      return { ok: false }
    }
    if (entry.isEndPending) {
      return { ok: false }
    }
    if (!entry.isHold) {
      return { ok: true, mode: 'reused' }
    }

    if (this.isResumePending) {
      return { ok: false }
    }

    entry.setResumePending(true)

    try {
      const { data } = await CallApi.updateVoiceCallUnhold({
        call_sid: entry.callSid,
        send_segment_event: sendSegmentEvent,
      })
      if (!data.success) {
        entry.setResumePending(false)
        const controlError = this._extractControlError(null, data)
        if (controlError) {
          this._dropStaleEntry(entry, controlError.message)
        }
        return { ok: false }
      }

      if (!data.mode || data.mode === 'reused') {
        entry.setIsHold(false)
        // Mirror the new_leg path (originateResumeLeg): clear the confirmed
        // -held flag too, otherwise a reused resume leaves isHoldConfirmed=true.
        entry.setHoldConfirmed(false)
        entry.setResumePending(false)
        return { ok: true, mode: 'reused' }
      }

      if (data.mode === 'new_leg') {
        if (!data.resume_token) {
          // new_leg without a token is unusable — keep parked, let the user retry.
          entry.setResumePending(false)
          return { ok: false }
        }
        // Keep isHold/isResumePending set: the resume is only committed once the
        // FE-originated leg actually connects (see originateResumeLeg), never on
        // this HTTP 200.
        return { ok: true, mode: 'new_leg', resumeToken: data.resume_token }
      }

      entry.setResumePending(false)
      return { ok: false }
    } catch (e) {
      entry.setResumePending(false)
      logger.error(e)
      const controlError = this._extractControlError(e)
      if (controlError) {
        this._dropStaleEntry(entry, controlError.message)
      }
      return { ok: false }
    }
  }

  private _extractControlError = (
    error: unknown,
    data?: IResponseVoiceCall
  ): { code: ICallControlErrorCode; message?: string } | null => {
    const body = isAxiosError<IResponseVoiceCall>(error) ? error.response?.data : data
    if (body?.code === 'call_not_found' || body?.code === 'call_leg_expired') {
      return { code: body.code, message: body.error }
    }
    return null
  }

  private _toastControlError = (message?: string) => {
    toastStore.add({
      type: 'error',
      title: 'Call no longer available',
      desc:
        message ||
        'This call has ended or belongs to a different account or session, and can no longer be controlled here.',
    })
  }

  private _dropStaleEntry = (entry: CallEntry, message?: string) => {
    this._sharedWorkerCallStore.notifyCallEnded(entry.callSid)
    runInAction(() => {
      this._removeEntry(entry.callSid)
      this._promoteNextPrimary()
    })
    if (this._callsMap.size === 0) {
      this._resetIdleState()
    }
    this._toastControlError(message)
  }

  // Public entry point so non-store callers (e.g. CallPopUp.handleDisconnect's
  // direct /call-end) react to call_not_found / call_leg_expired the same way
  // holdCall/unholdCall/disconnectCall do: drop the stale entry (or just toast
  // if it's already gone) instead of surfacing a generic failure. Returns true
  // when the error was a recognized control error so the caller can stop.
  handleControlError = (callSid: string, error: unknown, data?: IResponseVoiceCall): boolean => {
    const controlError = this._extractControlError(error, data)
    if (!controlError) return false

    const entry = this._lookupEntry(callSid)
    if (entry) {
      this._dropStaleEntry(entry, controlError.message)
    } else {
      this._toastControlError(controlError.message)
    }
    return true
  }

  markHoldConfirmed = (parentCallSid: string) => {
    const entry = this._callsMap.get(parentCallSid)
    if (!entry) return
    entry.setHoldConfirmed(true)
  }

  // call.resume_confirmed: Twilio actually executed the resume TwiML — the
  // authoritative "off hold" signal, vs the optimistic /unhold HTTP 200. The
  // reused-resume path already clears these on 200; this idempotently confirms.
  markResumeConfirmed = (callSid: string) => {
    const entry = this._lookupEntry(callSid)
    if (!entry) return
    entry.setIsHold(false)
    entry.setHoldConfirmed(false)
    entry.setResumePending(false)
  }

  markHeldCallEnded = (callSid: string) => {
    const entry = this._lookupEntry(callSid)
    if (!entry) return
    if (!entry.isHold && !entry.isHoldPending) return

    if (!entry.isSdkLegClosed) {
      entry.setEndPending(true)
      this._scheduleAnnouncementTeardownFallback(entry.callSid)
      return
    }

    this._sharedWorkerCallStore.notifyCallEnded(entry.callSid)
    runInAction(() => {
      this._removeEntry(entry.callSid)
      this._promoteNextPrimary()
    })
    if (this._callsMap.size === 0) {
      this._resetIdleState()
    }
  }

  private _resetIdleState = () => {
    this._sharedWorkerCallStore.setBusy(false)
    this.activeCallingStore.sendCallingEvent(false)
    this.activeCallingStore.sendConversationCallingEvent(false)
    this.hideCall()
    this._device?.audio?.unsetInputDevice()
  }

  sendDigitsTwilio = (value: string) => {
    this._connect?.sendDigits(value)
  }

  testOutputDevice = async () => {
    if (!this._device?.audio) return

    const selectedDevice = this.deviceSettingsStore.device.audioOutput
    if (selectedDevice?.deviceId) {
      try {
        await this._device.audio.ringtoneDevices.set(selectedDevice.deviceId)
      } catch (e) {
        logger.error(e)
      }
    }

    this._device.audio.ringtoneDevices.test()
  }

  stopStreamInputDeviceTrack = () => {
    if (this._device?.audio?.inputStream) {
      managerStream.stopTrack(this._device?.audio?.inputStream)
    }
  }

  stopStreams = () => {
    managerStream.stopTracks('twilio')

    const stream = this._connect?.getLocalStream()

    if (stream) {
      managerStream.stopTrack(stream)
    }

    managerStream.removeStreams('twilio')
  }

  handleMute = (status: boolean) => {
    if (!this._connect) return

    this._connect?.mute(status)
  }

  /**
   * Start the inbound ringtone on this tab.
   * Called by SharedWorkerCallStore when the worker has elected this tab
   * as the ringing tab for the given callSid.
   *
   * The check against the current Twilio call's SID guards against late
   * messages from the worker for a call that has already been replaced.
   */
  startRing = (callSid?: string) => {
    if (!this._device) return
    if (callSid && this._connect && this._connect.parameters.CallSid !== callSid) return

    if (this._isRingEverywhere && this._device.audio) {
      this.startRingEverywhere()
      return
    }

    // TODO: https://github.com/twilio/twilio-voice.js/issues/222
    this._device._soundcache.get(Device.SoundName.Incoming)?.play()
  }

  /**
   * Stop the inbound ringtone on this tab.
   * Called by the worker on accept/end/handoff and locally on the same
   * connect events as a safety net.
   */
  stopRing = (callSid?: string) => {
    if (!this._device) return
    if (callSid && this._connect && this._connect.parameters.CallSid !== callSid) return

    this.stopRingEverywhere()
    // TODO: https://github.com/twilio/twilio-voice.js/issues/222
    const sound = this._device._soundcache.get(Device.SoundName.Incoming)
    if (sound?.isPlaying) sound.stop()
  }

  handleMuteIncomingNotification = async (status: boolean) => {
    if (!this._device) return

    try {
      if (status) {
        this.stopRingEverywhere()
        // TODO: https://github.com/twilio/twilio-voice.js/issues/222
        this._device?._soundcache.get(Device.SoundName.Incoming).stop()
        this._device.audio?.incoming(status)
        return
      }

      if (this._isRingEverywhere && this._device.audio) {
        this.startRingEverywhere()
      } else {
        if (this._device.audio && !status) {
          const selectedDevice = this.deviceSettingsStore.device.audioOutput

          if (selectedDevice?.deviceId) {
            try {
              const availableDevices = Array.from(
                this._device.audio.availableOutputDevices.values()
              )
              const isDeviceAvailable = availableDevices.some(
                (device) => device.deviceId === selectedDevice.deviceId
              )

              if (!isDeviceAvailable) {
                const defaultDevice = availableDevices.find((d) => d.deviceId === 'default')
                if (defaultDevice) {
                  if (
                    this._device.audio.speakerDevices &&
                    typeof this._device.audio.speakerDevices.set === 'function'
                  ) {
                    await this._device.audio.speakerDevices.set(defaultDevice.deviceId)
                    await this._device.audio.ringtoneDevices.set(defaultDevice.deviceId)
                  }
                }
              } else {
                if (
                  this._device.audio.speakerDevices &&
                  typeof this._device.audio.speakerDevices.set === 'function'
                ) {
                  await this._device.audio.speakerDevices.set(selectedDevice.deviceId)
                  await this._device.audio.ringtoneDevices.set(selectedDevice.deviceId)
                }
              }
            } catch (e) {
              logger.error(`[handleMuteIncomingNotification] Failed to set output device: ${e}`)
            }
          }
        }

        this._device.audio?.incoming(status)
        this._device?._soundcache.get(Device.SoundName.Incoming)?.play()
      }
    } catch (e) {
      logger.error(e)
    }
  }

  startRingEverywhere = async () => {
    if (!this._isRingEverywhere) {
      if (this._device?.audio) {
        this._device.audio.incoming(false)
        this._device?._soundcache.get(Device.SoundName.Incoming).play()
      }
      return
    }

    if (!this._device?.audio) {
      this._device?.audio?.incoming(false)
      return
    }

    this.stopRingEverywhere()
    this._ringEverywhereStopped = false

    try {
      const deviceAudio = this._device.audio
      const outputDevices = Array.from(deviceAudio.availableOutputDevices.values())

      if (outputDevices.length === 0) {
        this._device.audio.incoming(false)
        return
      }

      const incomingSound = this._device?._soundcache.get(Device.SoundName.Incoming)
      if (!incomingSound) {
        this._device.audio.incoming(false)
        return
      }

      const sound: TwilioSound = incomingSound
      const soundAudio = sound._audio
      const soundSrc = sound._src
      const soundUrl = sound.url

      let audioSrc = soundAudio?.src || soundAudio?.currentSrc || soundSrc || soundUrl

      if (
        audioSrc &&
        (audioSrc.includes(window.location.hostname) || audioSrc.startsWith(window.location.origin))
      ) {
        audioSrc = soundUrl || soundSrc || undefined
      }

      if (!audioSrc && soundAudio) {
        try {
          await soundAudio.play()
          await new Promise((resolve) => setTimeout(resolve, 100))
          soundAudio.pause()
          soundAudio.currentTime = 0
          const newSrc = soundAudio.src || soundAudio.currentSrc
          if (
            newSrc &&
            !newSrc.includes(window.location.hostname) &&
            !newSrc.startsWith(window.location.origin)
          ) {
            audioSrc = newSrc
          }
        } catch (e) {
          logger.error(`Failed to trigger sound load: ${e}`)
        }

        if (!audioSrc) {
          await new Promise<void>((resolve) => {
            const checkInterval = setInterval(() => {
              const newSrc = soundAudio.src || soundAudio.currentSrc
              if (
                newSrc &&
                !newSrc.includes(window.location.hostname) &&
                !newSrc.startsWith(window.location.origin)
              ) {
                audioSrc = newSrc
                clearInterval(checkInterval)
                resolve()
              }
            }, 100)

            setTimeout(() => {
              clearInterval(checkInterval)
              resolve()
            }, 3000)
          })
        }
      }

      if (
        audioSrc &&
        (audioSrc.includes(window.location.hostname) || audioSrc.startsWith(window.location.origin))
      ) {
        audioSrc = soundUrl || audioSrc
      }

      if (!audioSrc || audioSrc === window.location.href) {
        this._device.audio.incoming(false)
        return
      }

      const setupPromises = outputDevices.map(async (outputDevice) => {
        try {
          const audio = new Audio()
          audio.loop = true
          audio.volume = 1.0
          audio.preload = 'auto'

          const audioWithSinkId: HTMLAudioElementWithSinkId = audio

          if (audioWithSinkId.setSinkId && typeof audioWithSinkId.setSinkId === 'function') {
            try {
              await audioWithSinkId.setSinkId(outputDevice.deviceId)

              if (typeof audioSrc === 'string' && audioSrc && audioSrc !== window.location.href) {
                audio.src = audioSrc
              } else {
                logger.error('[startRingEverywhere] audioSrc is not a valid string')
                return null
              }

              const loadTimeout = 8000

              await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => {
                  logger.error(`[${outputDevice.label}] Load timeout after ${loadTimeout}ms`)
                  reject(new Error('Load timeout'))
                }, loadTimeout)

                audio.addEventListener(
                  'canplaythrough',
                  () => {
                    clearTimeout(timeout)
                    resolve()
                  },
                  { once: true }
                )

                audio.addEventListener(
                  'error',
                  (e) => {
                    clearTimeout(timeout)
                    logger.error(`[${outputDevice.label}] Audio error event: ${e}`)
                    reject(e)
                  },
                  { once: true }
                )

                audio.addEventListener('loadeddata', () => {}, { once: true })
                audio.load()
              })

              return { audio, device: outputDevice }
            } catch (e) {
              logger.error(
                `[${outputDevice.label}] Failed to setup audio: ${
                  e instanceof Error ? e.message : e
                }`
              )
              return null
            }
          } else {
            logger.warn(`[${outputDevice.label}] setSinkId not supported by browser`)
            return null
          }
        } catch (e) {
          logger.error(`[${outputDevice.label}] Error setting up device: ${e}`)
          return null
        }
      })

      const audioElementsWithDevices = (await Promise.all(setupPromises)).filter(
        (item): item is { audio: HTMLAudioElement; device: MediaDeviceInfo } => item !== null
      )
      if (this._ringEverywhereStopped) {
        audioElementsWithDevices.forEach(({ audio }) => {
          try {
            audio.pause()
            audio.src = ''
            audio.load()
          } catch (e) {
            logger.error(`[startRingEverywhere] Failed to cleanup audio: ${e}`)
          }
        })
        return
      }

      if (audioElementsWithDevices.length > 0) {
        this._ringEverywhereAudioElements = audioElementsWithDevices.map((item) => item.audio)

        if (this._ringEverywhereStopped) {
          this.stopRingEverywhere()
          return
        }

        const playPromises = audioElementsWithDevices.map(async ({ audio, device }) => {
          try {
            await audio.play()
          } catch (e) {
            logger.error(
              `[${device.label}] Failed to play audio: ${e instanceof Error ? e.message : e}`
            )
          }
        })

        await Promise.allSettled(playPromises)
      } else {
        if (this._device?.audio) {
          this._device.audio.incoming(false)
        }
      }
    } catch (e) {
      logger.error(`Error in startRingEverywhere: ${e instanceof Error ? e.message : e}`)
      if (this._device?.audio) {
        this._device.audio.incoming(false)
      }
    }
  }

  stopRingEverywhere = () => {
    this._ringEverywhereStopped = true

    if (this._ringEverywhereAudioElements.length > 0) {
      this._ringEverywhereAudioElements.forEach((audio, index) => {
        try {
          audio.pause()
          audio.currentTime = 0
          audio.src = ''
          audio.load()
        } catch (e) {
          logger.error(`[stopRingEverywhere] Failed to stop audio element ${index + 1}: ${e}`)
        }
      })
      this._ringEverywhereAudioElements = []
    }
  }

  handleChangeRecord = async () => {
    try {
      runInAction(() => {
        this._isRecord = !this._isRecord
        this._isRecordLoading = true
      })

      await UsersApi.updateUsersToggleRecordAutomatically({
        isRecordAutomatically: this._isRecord,
      })

      runInAction(() => {
        this._usersStore.isRecordAutomatically = this._isRecord
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isRecordLoading = false
      })
    }
  }

  handleChangeRingEverywhere = async () => {
    try {
      runInAction(() => {
        this._isRingEverywhere = !this._isRingEverywhere
        this._isRingEverywhereLoading = true
      })

      await UsersApi.updateUsersToggleRingEverywhere({
        isRingEverywhere: this._isRingEverywhere,
      })

      runInAction(() => {
        this._usersStore.isRingEverywhere = this._isRingEverywhere
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._isRingEverywhereLoading = false
      })
    }
  }

  setStartedCall = (value: boolean) => {
    this._started = value
  }

  reactionSelectedAudioOutput = () => {
    this._disposeSelectedAudioOutput?.()
    this._disposeSelectedAudioOutput = reaction(
      () => this.deviceSettingsStore.device.audioOutput,
      (value) => {
        if (!value) return
        if (this._status !== Call.State.Closed) return

        this.setOutputDevice(value.deviceId)
      },
      {
        fireImmediately: true,
      }
    )
  }

  reactionSelectedAudioInput = () => {
    this._disposeSelectedAudioInput?.()
    this._disposeSelectedAudioInput = reaction(
      () => this.deviceSettingsStore.device.audioInput,
      (value) => {
        if (!value) return

        this.setInputDevice(value.deviceId)
      },
      {
        fireImmediately: true,
      }
    )
  }

  disposeAudioReaction = () => {
    this._disposeSelectedAudioOutput?.()
    this._disposeSelectedAudioInput?.()
  }

  reactionStatus = () => {
    this._disposeStatus?.()
    this._disposeStatus = reaction(
      () => this._status,
      async (value) => {
        if (value === Call.State.Open) {
          this.setStartedCall(true)
        }
        if (value === Call.State.Closed) {
          this.setStartedCall(false)
        }
      }
    )
  }

  reactionPrimaryPromotion = () => {
    this._disposePrimaryPromotion?.()
    this._disposePrimaryPromotion = reaction(
      () => this.primaryEntry?.callSid ?? null,
      (currentSid, prevSid) => {
        if (prevSid === currentSid) return

        this._promoteNextPrimary()
      }
    )
  }

  reactionUserSettings = () => {
    this._disposeUserSettings?.()
    this._disposeUserSettings = reaction(
      () => ({
        isRingEverywhere: this._usersStore.isRingEverywhere,
        isRecordAutomatically: this._usersStore.isRecordAutomatically,
      }),
      (value) => {
        runInAction(() => {
          this._isRingEverywhere = value.isRingEverywhere
          this._isRecord = value.isRecordAutomatically
        })
      }
    )
  }

  get isIncoming() {
    return this._direction === Call.CallDirection.Incoming
  }

  get isOutgoing() {
    return this._direction === Call.CallDirection.Outgoing
  }

  get isStatusClosed() {
    return this._status === Call.State.Closed
  }

  get isAcceptVoice() {
    if (this.isIncoming) {
      return this._status === Call.State.Open
    }

    return this.isOutgoing
  }

  get isShowCall() {
    if (this._inboxesStore.currentInbox?.type !== 'inbox') return true

    return this._inboxesStore.currentInbox?.isNumberOutboundCalls
  }

  get isStatusClose() {
    return this._status === Call.State.Closed
  }

  get isStatusOpen() {
    return this._status === Call.State.Open
  }

  get isBusy() {
    return this._started || this._status !== Call.State.Closed
  }

  get device() {
    return this._device
  }

  get connect() {
    return this._connect
  }

  get callsMap() {
    return this._callsMap
  }

  get allCallEntries(): CallEntry[] {
    return Array.from(this._callsMap.values())
  }

  get primaryEntry(): CallEntry | null {
    const entries = this.allCallEntries
    if (entries.length === 0) return null

    const active = entries
      .filter((entry) => entry.isActive && !entry.isHold)
      .sort((a, b) => a.addedAt - b.addedAt)
    if (active.length) return active[0]

    const held = entries
      .filter((entry) => entry.isHold || entry.isEndPending)
      .sort((a, b) => a.addedAt - b.addedAt)
    if (held.length) return held[held.length - 1]

    const ringing = entries.filter((entry) => entry.isRinging).sort((a, b) => a.addedAt - b.addedAt)
    if (ringing.length) return ringing[0]

    return null
  }

  get ringingEntries(): CallEntry[] {
    const primarySid = this.primaryEntry?.callSid
    return this.allCallEntries
      .filter((entry) => entry.isRinging && entry.callSid !== primarySid)
      .sort((a, b) => a.addedAt - b.addedAt)
  }

  get heldEntries(): CallEntry[] {
    const primarySid = this.primaryEntry?.callSid
    return this.allCallEntries
      .filter((entry) => entry.isHold && entry.callSid !== primarySid)
      .sort((a, b) => a.addedAt - b.addedAt)
  }

  get isResumePending(): boolean {
    return this.allCallEntries.some((entry) => entry.isResumePending)
  }

  getEntry = (callSid: string): CallEntry | undefined => {
    return this._lookupEntry(callSid)
  }

  private _removeEntry = (callSid: string) => {
    const entry = this._callsMap.get(callSid)
    if (!entry) return
    this._clearAnnouncementTeardownFallback(entry.callSid)
    this._abandonPendingResumeLegsFor(callSid)
    entry.dispose()
    this._callsMap.delete(callSid)
  }

  // Deferred-teardown safety net for a held call whose customer hung up while
  // the agent's live leg is still playing the exit announcement (see
  // markHeldCallEnded / endCallFromServer). Normally Twilio closes the leg once
  // the announcement finishes and eventConnectDisconnect tears down; this only
  // fires if that never arrives, so the popup can't hang open forever.
  private _scheduleAnnouncementTeardownFallback = (callSid: string) => {
    if (this._endPendingTimeouts.has(callSid)) return
    const timeoutId = setTimeout(() => {
      this._endPendingTimeouts.delete(callSid)
      const entry = this._lookupEntry(callSid)
      if (!entry || !entry.isEndPending) return
      this._teardownEndedCall(entry, entry.callSid)
    }, this._ANNOUNCEMENT_TEARDOWN_TIMEOUT_MS)
    this._endPendingTimeouts.set(callSid, timeoutId)
  }

  private _clearAnnouncementTeardownFallback = (callSid: string) => {
    const timeoutId = this._endPendingTimeouts.get(callSid)
    if (timeoutId === undefined) return
    clearTimeout(timeoutId)
    this._endPendingTimeouts.delete(callSid)
  }

  private _promoteNextPrimary = () => {
    const next = this.primaryEntry
    if (!next) {
      this._connect = null
      this._status = Call.State.Closed
      this._direction = null
      return
    }

    const prevInboxId = this._contactFrom?.id ?? null
    const prevConnect = this._connect

    this._connect = next.call
    this._contactTo = next.contact
    this._contactFrom = next.inbox
    this._twilioConversation = next.conversation
    this._status = next.isSdkLegClosed && next.isHold ? Call.State.Open : next.call.status()
    this._direction = next.call.direction

    // Only re-announce when the primary leg actually changed. reactionPrimaryPromotion
    // re-invokes _promoteNextPrimary after the primary SID changes, so without this
    // guard a single promotion would emit two identical signals (the re-entrant call
    // sees the same _connect and would fire again).
    const primaryChanged = prevConnect !== next.call
    const inboxChanged = prevInboxId !== null && next.inbox.id !== prevInboxId
    // A ringing INCOMING entry promoted to primary (e.g. a simultaneous inbound
    // that outlived the call the user just left/ended) must be re-announced so
    // the popup re-initialises as a full-screen incoming. Same-inbox promotions
    // skip the inboxChanged signal, so key off the ringing state explicitly —
    // otherwise the item keeps its stale (conference) context and renders blank.
    // Restricted to incoming: outgoing "ringing" (dialing) legs — e.g. power
    // dialer — must not be re-signalled, to avoid resetting their popup view.
    const promotedRingingIncoming =
      next.isRinging && next.call.direction === Call.CallDirection.Incoming
    if (primaryChanged && (inboxChanged || promotedRingingIncoming)) {
      this._signal = {
        key: nanoid(),
        name: next.call.direction === Call.CallDirection.Incoming ? 'incoming' : 'outgoing',
      }
    }
  }

  get loading() {
    return this._loading
  }

  get disabled() {
    return this._disabled
  }

  get voicemailDropCompleted() {
    return this._voicemailDropCompleted
  }

  get contactTo() {
    return this._contactTo
  }

  get contactFrom() {
    return this._contactFrom
  }

  get status() {
    return this._status
  }

  get isRecord() {
    return this._isRecord
  }

  get isRingEverywhere() {
    return this._isRingEverywhere
  }

  get isRingEverywhereLoading() {
    return this._isRingEverywhereLoading
  }

  get isRecordLoading() {
    return this._isRecordLoading
  }

  get signal() {
    return this._signal
  }

  get startedCall() {
    return this._started
  }

  get conversationId() {
    return this._twilioConversation?.id ?? null
  }

  get inboxId() {
    return this._twilioConversation?.inbox_id ?? this._contactFrom?.id ?? null
  }

  get numberId() {
    return this._twilioConversation?.number_id ?? this._contactFrom?.numberId ?? null
  }

  get twilioConversation() {
    return this._twilioConversation
  }

  isInboxOnCall = (inboxId: number): boolean => {
    return this.activeCallingStore.isInboxOnCall(inboxId)
  }
}
